import {Storage} from "aws-amplify";
import dayjs from 'dayjs';
import {snakeCase} from 'lodash';
// AWS.config.update({
//     region: config.s3.REGION,
//     credentials: new AWS.CognitoIdentityCredentials({
//         IdentityPoolId: config.cognito.IDENTITY_POOL_ID,
//     }),
// });
//
// const s3 = new AWS.S3({
//     apiVersion: "2006-03-01",
//     params: {Bucket: config.s3.BUCKET},
// });

export async function s3Upload(file, filename, options) {
    options = options || {}
    if (options.level === 'private') {
        return await s3UploadPrivateDirectly(file, filename, options);
    }
    const stored = await Storage.vault.put(filename, file, {
        ...options,
        contentType: file.type,
    });

    return stored.key;
}

// export async function s3Photo(photoKey, file, operatorId, secure) {
//     (await Auth.currentSession()).
//     return new AWS.S3.ManagedUpload({
//         params: {
//             Bucket: config.s3.BUCKET,
//             Key: `${secure ? 'private' : 'public'}/${operatorId}/${photoKey}`,
//             Body: file,
//         },
//     }).promise()
// }

export async function s3UploadPrivateDirectly(file, filename, options) {
    options = options || {}
    let stored;
    try {
        stored = await Storage.put(filename.replaceAll(' ', '_'), file, {
            contentType: options.contentType || file.type,
            customPrefix: {
                public: 'private/'
            },
            completeCallback: (event) => {
                console.log(`Successfully uploaded ${event.key}`);
            },
            progressCallback: (progress) => {
                console.log(`Uploaded: ${progress.loaded}/${progress.total}`);
            },
            errorCallback: (err) => {
                console.error('Unexpected error while uploading', err);
            },
            ...options,
        });
    } catch (e) {
        console.error('Error uploading file', e);
        throw e;
    }

    return stored.key;
}


const FILENAME_KEY_DELIMITER = '.';

/**
 * Creates timestamp components for S3 partitioning
 * @param {number} timestamp - Unix timestamp in milliseconds
 * @returns {Object} Object with year, month, day, hour, and timeStr
 */
const createTimestampComponents = (timestamp) => {
    const now = dayjs(timestamp);
    return {
        now,
        year: now.format('YYYY'),
        month: now.format('MM'),
        day: now.format('DD'),
        hour: now.format('HH'),
        timeStr: now.format('HHmmss')
    };
};

/**
 * Creates partition path components for S3 keys
 * @param {string} orgId - Organisation ID
 * @param {string} regionId - Region ID
 * @param {string} year - Year (YYYY)
 * @param {string} month - Month (MM)
 * @param {string} day - Day (DD)
 * @param {string} hour - Hour (HH)
 * @returns {Object} Object with partition path components
 */
const createPartitionPath = (orgId, regionId, year, month, day, hour) => {
    return {
        orgId,
        regionId,
        year,
        month,
        day,
        hour,
        partitionYear: parseInt(year),
        partitionMonth: parseInt(month),
        partitionDay: parseInt(day),
        partitionHour: parseInt(hour)
    };
};

/**
 * Creates base partition data structure common to all S3 saves
 * @param {Object} params - Base partition data parameters
 * @param {string} params.orgId - Organisation ID
 * @param {string} params.regionId - Region ID
 * @param {string} params.username - Username
 * @param {Object} params.partition - Partition path components
 * @returns {Object} Base partition data structure
 */
const createBasePartitionData = ({orgId, regionId, username, partition}) => {
    return {
        // Partition keys (duplicated for easier querying without joins)
        organisation_id: orgId,
        region_id: regionId,
        username: username,

        // Original partition info
        partition_year: partition.partitionYear,
        partition_month: partition.partitionMonth,
        partition_day: partition.partitionDay,
        partition_hour: partition.partitionHour
    };
};

/**
 * Generic function to save data to S3 with Hive-style partitioning
 * @param {Object} params - S3 save parameters
 * @param {string} params.basePath - Base S3 path (e.g., 'private/errors' or 'private/history')
 * @param {Function} params.buildKeyFilename - Function to build the filename part of the S3 key
 * @param {Function} params.buildData - Function to build the data object to save
 * @param {Object} params.metadata - S3 metadata object
 * @param {string} params.orgId - Organisation ID
 * @param {string} params.regionId - Region ID
 * @param {string} params.username - Username
 * @param {number} params.timestamp - Timestamp in milliseconds
 * @param {Function} [params.logger] - Optional logger function
 * @param {Function} [params.errorLogger] - Optional error logger function
 * @param {Function} [params.buildErrorKey] - Optional function to build S3 key for error messages
 * @returns {Promise<string|null>} Returns the S3 key if successful, null if failed
 */
const saveToS3 = async ({
    basePath,
    buildKeyFilename,
    buildData,
    metadata,
    orgId,
    regionId,
    username,
    timestamp = Date.now(),
    logger = console.log,
    errorLogger = console.error,
    buildErrorKey
}) => {
    try {
        const {now, year, month, day, hour, timeStr} = createTimestampComponents(timestamp);
        const partition = createPartitionPath(orgId, regionId, year, month, day, hour);
        
        // Build the full S3 key
        const filename = buildKeyFilename({orgId, regionId, year, month, day, hour, timeStr, partition});
        const s3Key = `${basePath}/organisation_id=${orgId}/region_id=${regionId}/year=${year}/month=${month}/day=${day}/hour=${hour}/${filename}`;
        
        // Build the data object with base partition data
        const baseData = createBasePartitionData({orgId, regionId, username, partition});
        const data = buildData({baseData, now, timestamp, partition});

        logger(`[saveToS3] Saving to S3, key: ${s3Key}`);
        const jsonString = JSON.stringify(data);
        const storedKey = await s3UploadPrivateDirectly(jsonString, s3Key, {
            contentType: 'application/json',
            metadata
        });
        logger(`[saveToS3] Successfully saved to S3, key: ${storedKey}`);
        
        return storedKey;
    } catch (e) {
        // Try to construct s3Key for error message
        let s3KeyForError = 'unknown';
        try {
            if (buildErrorKey) {
                const {year, month, day, hour, timeStr} = createTimestampComponents(timestamp);
                const partition = createPartitionPath(orgId, regionId, year, month, day, hour);
                const filename = buildErrorKey({orgId, regionId, year, month, day, hour, timeStr, partition});
                s3KeyForError = `${basePath}/organisation_id=${orgId}/region_id=${regionId}/year=${year}/month=${month}/day=${day}/hour=${hour}/${filename}`;
            }
        } catch {
            // If we can't construct the key, just use a generic message
        }
        errorLogger(`[saveToS3] Failed to save to S3: ${e.message}, key: ${s3KeyForError}, error: ${e}`);
        return null;
    }
};


/**
 * Recursively convert all object keys to snake_case
 * @param {*} obj - The object to convert
 * @returns {*} The object with all keys converted to snake_case
 */
const convertKeysToSnakeCase = (obj) => {
    if (obj === null || obj === undefined) {
        return obj;
    }
    
    if (Array.isArray(obj)) {
        return obj.map(item => convertKeysToSnakeCase(item));
    }
    
    if (typeof obj !== 'object') {
        return obj;
    }
    
    const converted = {};
    for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
            const snakeKey = snakeCase(key);
            converted[snakeKey] = convertKeysToSnakeCase(obj[key]);
        }
    }
    return converted;
};

export const saveLoadPerformanceToS3 = async ({
    performanceSnapshot,
    orgId,
    regionId,
    eventType = 'LOAD_PERFORMANCE',
    username,
    url = 'unknown',
    timestamp = Date.now(),
    logger = console.log,
    errorLogger = console.error
}) => {
    // Convert all keys in performanceSnapshot to snake_case
    const snakeCaseSnapshot = convertKeysToSnakeCase(performanceSnapshot);

    if(performanceSnapshot.loadType === 'delta') {
        eventType = 'DELTA_LOAD_PERFORMANCE';
    } else if(performanceSnapshot.loadType === 'full') {
        eventType = 'FULL_LOAD_PERFORMANCE';
    }
    
    // URL encode components to handle special characters (e.g., dots in email addresses)
    return await saveToS3({
        basePath: 'history',
        buildKeyFilename: ({timeStr}) => `${eventType}${FILENAME_KEY_DELIMITER}${timeStr}.json`,
        buildData: ({baseData, now, timestamp, partition}) => ({
            event_type: eventType,
            event_timestamp: now.toISOString(),
            event_timestamp_unix: timestamp,
            username,
            url,
            ...partition,
            ...baseData,
            ...snakeCaseSnapshot,
        }),
        metadata: {
            'event-type': eventType,
            'organisation-id': orgId,
            'region-id': regionId,
            'username': username,
        },
        orgId,
        regionId,
        username,
        timestamp,
        logger: (msg) => logger(`[saveLoadPerformanceToS3] ${msg}`),
        errorLogger: (msg) => errorLogger(`[saveLoadPerformanceToS3] ${msg}`),
        buildErrorKey: ({timeStr}) => `${eventType}${FILENAME_KEY_DELIMITER}${timeStr}.json`
    });
};

